/**
 * Shared helpers for syncing list-page state (search, filter chips, sort,
 * pagination) into URL search params. Each list page wraps these with its
 * own filter-field semantics — these helpers cover only the generic bits.
 */

export type SortDirection = 'asc' | 'desc';

export interface SortToken {
  field: string;
  direction: SortDirection;
}

/** Read an integer param, falling back when missing, empty, or non-finite,
 *  and clamping to the optional `[min, max]` range. Fractional values are
 *  floored (so `?page=2.7` becomes `2`). Negative or out-of-range values
 *  clamp to the nearest bound. */
export const parseNumberParam = (
  searchParams: URLSearchParams,
  key: string,
  fallback: number,
  options?: { min?: number; max?: number }
): number => {
  const raw = searchParams.get(key);
  if (raw === null || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  let intN = Math.floor(n);
  if (options?.min !== undefined && intN < options.min) intN = options.min;
  if (options?.max !== undefined && intN > options.max) intN = options.max;
  return intN;
};

/** Read every value for a repeated key, dropping empty strings. */
export const parseStringListParam = (searchParams: URLSearchParams, key: string): string[] =>
  searchParams.getAll(key).filter((v) => v !== '');

/**
 * Parse `?sort=field:direction`. Returns `null` when the key is missing or
 * the direction is not asc/desc. Field names are passed through unchanged
 * — callers should validate against their domain's known sort fields if
 * untrusted input is a concern.
 */
export const parseSortParam = (searchParams: URLSearchParams): SortToken | null => {
  const raw = searchParams.get('sort');
  if (!raw) return null;
  const idx = raw.lastIndexOf(':');
  if (idx <= 0) return null;
  const field = raw.slice(0, idx);
  const direction = raw.slice(idx + 1);
  if (!field || (direction !== 'asc' && direction !== 'desc')) return null;
  return { field, direction };
};

/** Mutate `params`: clear `key`, then re-add each non-empty value. */
export const setStringListParam = (
  params: URLSearchParams,
  key: string,
  values: ReadonlyArray<string>
): void => {
  params.delete(key);
  for (const v of values) {
    if (v) params.append(key, v);
  }
};

/** Mutate `params`: set `key` when the value is truthy, delete otherwise. */
export const setOptionalParam = (
  params: URLSearchParams,
  key: string,
  value: string | null | undefined
): void => {
  if (value) params.set(key, value);
  else params.delete(key);
};

/** Mutate `params`: set `?sort=field:direction` or delete when token is null. */
export const setSortParam = (params: URLSearchParams, token: SortToken | null): void => {
  if (token && token.field) {
    params.set('sort', `${token.field}:${token.direction}`);
  } else {
    params.delete('sort');
  }
};
